import { useState, useContext, useEffect } from "react";
import {
  Table,
  Thead,
  Tbody,
  Tr,
  TableContainer,
  Box,
  Td,
  Button,
  Spinner,
  Center,
  Flex,
} from "@chakra-ui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import NoResults from "components/elements/NoResults";
import ColumnsPopover from "components/loans/ColumnsPopover";
import { LoansContext } from "providers/loans";
import { useTenant } from "modules/tenant/providers/tenant-provider";
import { Link } from "react-router-dom";
import { FIXED_FIELDS } from "utils/constants";
import { newLoanAppPermission } from "utils/permissionHelper";
import { FormatTableCell, getFormattedContent } from "./loans-table-cell";
import { LoanColumns } from "./loans-table-columns";
import { getCustomButtonVariant } from "utils/uiconfighelper";
import { DownloadLink } from "./download-link";
import { LoanModel } from "providers/loans/loan.model";
import { Pagination } from "components/elements/Pagination/pagination";
import InputSearch from "components/elements/InputSearch/input-search";
import colors from "utils/colors";
import { useCustomFormats } from "hooks/useCustomFormat";

const PAGE_SIZE = 10;

export function sortLoanFields(a: any, b: any) {
  return a.order - b.order;
}

const DEFAULT_COLUMNS = [
  "name",
  "borrower",
  "type",
  "status",
  "amount",
  "created_at",
  "funding_date",
];

const NewApplicationLink = ({ newAppPermission, user }: { newAppPermission: any; user: any }) => (
  <Link to="/loans/application">
    {newAppPermission && (
      <Button
        data-cy="new-application-btn"
        mr="12px"
        variant={getCustomButtonVariant(user)}
        leftIcon={<FontAwesomeIcon icon={["far", "plus"]} />}
      >
        New Application
      </Button>
    )}
  </Link>
);

const formatColumn = (field: any) => {
  return {
    id: field.id || field.data_key,
    label: field.label,
    type: field.type,
    data_key: field.data_key,
    order: field.order,
    show: DEFAULT_COLUMNS.includes(field.data_key),
  };
};

export function LoansTable() {
  const { loans, loadingLoans, orderBy, setOrderBy } = useContext(LoansContext);
  const { user } = useTenant();
  const { formatDate, formatCurrency } = useCustomFormats();
  const [columns, setColumns] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRows, setSelectedRows] = useState<any[]>([]);
  const [selectAllChecked, setSelectAllChecked] = useState(false);
  const newAppPermission = newLoanAppPermission(user) === true;

  const [searchQuery, setSearchQuery] = useState("");
  const [filteredLoans, setFilteredLoans] = useState<LoanModel[]>([]);

  const columnsToFilter = columns.filter(col => col.show && col.label !== "Select");

  const valuesToFilter = loans.map(loan => {
    return columnsToFilter.map(col => {
      const value =
        col.data_key === "Select"
          ? "checkbox"
          : col.data_key
          ? loan[col.data_key as keyof LoanModel]
          : loan.values.find((val: any) => val.loan_field.id === col.id)?.value?.field;

      return {
        loanId: loan.id,
        value: getFormattedContent(col.type, value, loan, selectedRows, formatDate, formatCurrency)
          .value,
      };
    });
  });

  useEffect(() => {
    if (user.type === "lender") {
      setColumns([
        ...FIXED_FIELDS,
        ...user.company.fields.sort((a: any, b: any) => a.order - b.order).map(formatColumn),
      ]);
    } else {
      setColumns([
        ...FIXED_FIELDS.filter((field: any) => field.data_key !== "borrower"),
        ...user.lender_company.fields
          .sort((a: any, b: any) => a.order - b.order)
          .filter((field: any) => !field.underwriting)
          .map(formatColumn),
      ]);
    }
  }, []);

  useEffect(() => {
    if (searchQuery.trim() === "") {
      setFilteredLoans(loans);
      return;
    }

    const searchTerm = searchQuery.toLowerCase().trim();

    // Get all loanIds that match the search term in any of their values
    const matchingLoanIds = valuesToFilter
      .flatMap(loanValues => loanValues)
      .filter(item => {
        const value = String(item.value).toLowerCase();
        return value.includes(searchTerm);
      })
      .map(item => item.loanId);

    // Filter loans by the matching IDs
    const filtered = loans.filter(loan => matchingLoanIds.includes(loan.id));

    setFilteredLoans(filtered);
    setCurrentPage(1);
  }, [loans, searchQuery, valuesToFilter]);

  const totalPages = Math.ceil(filteredLoans.length / PAGE_SIZE);
  const startIndex = (currentPage - 1) * PAGE_SIZE;
  const visibleLoans = filteredLoans.slice(startIndex, startIndex + PAGE_SIZE);

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const handleSelectAllRows = () => {
    const allIds: string[] = filteredLoans.map((obj: any) => obj.id);
    const allVisibleRowsSelected = selectedRows.length === allIds.length;
    const updatedSelection = allVisibleRowsSelected ? [] : allIds;
    setSelectAllChecked(!allVisibleRowsSelected);
    setSelectedRows(updatedSelection);
  };

  const handleRowSelect = (id: string) => {
    setSelectedRows(prevSelectedRows => {
      const updatedSelection = prevSelectedRows.includes(id)
        ? prevSelectedRows.filter(rowId => rowId !== id)
        : [...prevSelectedRows, id];
      setSelectAllChecked(updatedSelection.length === filteredLoans.length);
      return updatedSelection;
    });
  };

  const selectedLoans = loans.filter((loan: any) => selectedRows.includes(loan.id));
  const filteredColumns = columns.filter(
    column => column.show && column.label !== "Select" && column.label !== "Loan Name"
  );

  const getRowBackground = (id: any) => {
    if (selectedRows.length > 0) {
      const isSelected = selectedRows.includes(id);
      if (isSelected) return colors.GRAY_50;
      else return "";
    } else {
      return "";
    }
  };

  return (
    <Flex flexDirection="column" gap={4}>
      <Flex gap={4} justifyContent={"space-between"}>
        {/* Search Bar */}
        <InputSearch
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          width="300px"
        />

        {/* Buttons */}
        <Box display={"flex"}>
          {selectedRows.length > 0 && (
            <DownloadLink loans={selectedLoans} columns={filteredColumns} />
          )}
          {/* New Application Button */}
          <NewApplicationLink newAppPermission={newAppPermission} user={user} />
          <ColumnsPopover fixedFields={FIXED_FIELDS} columns={columns} setColumns={setColumns} />
        </Box>
      </Flex>

      {/* Table */}
      <TableContainer>
        <Table>
          <Thead>
            <LoanColumns
              columns={columns}
              orderBy={orderBy}
              setOrderBy={setOrderBy}
              onRowSelect={handleSelectAllRows}
              selectAllCheck={selectAllChecked}
            />
          </Thead>
          {loans.length === 0 || loadingLoans ? (
            <Tbody>
              <Tr>
                <Td colSpan={columns.filter(col => col.show).length}>
                  {loadingLoans ? (
                    <Center>
                      <Spinner />
                    </Center>
                  ) : (
                    <NoResults loading={loadingLoans} message="No loans found." />
                  )}
                </Td>
              </Tr>
            </Tbody>
          ) : (
            <Tbody>
              {visibleLoans.map((loan: any) => (
                <Tr
                  data-cy="loans-table-row"
                  key={loan.id}
                  backgroundColor={getRowBackground(loan.id)}
                  cursor="pointer"
                >
                  {columns
                    .filter(col => col.show)
                    .map(col => (
                      <FormatTableCell
                        loan={loan}
                        key={col.id}
                        type={col.type}
                        value={
                          col.data_key === "Select"
                            ? "checkbox"
                            : col.data_key
                            ? loan[col.data_key]
                            : loan.values.find((val: any) => val.loan_field.id === col.id)?.value
                                ?.field
                        }
                        onRowSelect={handleRowSelect}
                        selectedRows={selectedRows}
                      />
                    ))}
                </Tr>
              ))}
            </Tbody>
          )}
        </Table>
      </TableContainer>

      <Pagination
        totalPages={totalPages}
        currentPage={currentPage}
        handlePageChange={handlePageChange}
        totalCount={filteredLoans.length}
        pageSize={PAGE_SIZE}
      />
    </Flex>
  );
}
